import { CommonModule } from "@angular/common";
import { OverlayModule } from "@angular/cdk/overlay";
import { MatIconModule } from "@angular/material/icon";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	TemplateRef,
	ViewChild,
} from "@angular/core";

/**
 * Autocomplete del design-system `.ds` (sin componentes Material visuales).
 *
 * Presentacional + comportamiento: input `.ds` + desplegable propio posicionado con
 * CDK Overlay (headless), filtrado por texto, navegación con teclado y click-fuera.
 * La carga de datos y el binding a formularios los gestiona el wrapper que lo usa
 * (p.ej. `app-actividad-autocomplete`), que sigue siendo el `ControlValueAccessor`.
 *
 * Contrato: recibe `options` + `displayWith`; expone el objeto seleccionado por
 * `[value]`/`(valueChange)` y `(touched)`. No conoce ids ni servicios.
 */
@Component({
	selector: "app-ds-autocomplete",
	standalone: true,
	imports: [CommonModule, OverlayModule, MatIconModule],
	templateUrl: "./ds-autocomplete.component.html",
	styleUrls: ["./ds-autocomplete.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsAutocompleteComponent {
	@Input() label = "";
	@Input() placeholder = "";
	@Input() hint = "";
	@Input() required = false;
	@Input() noResults = "Sin resultados";

	@Input() set disabled(value: boolean) {
		this._disabled = value;
		if (value) {
			this.close();
		}
	}
	get disabled(): boolean {
		return this._disabled;
	}
	private _disabled = false;

	/** Lista completa de opciones (el filtrado por texto lo hace este componente). */
	@Input() options: any[] = [];

	/** Texto a mostrar para una opción (input + fila del desplegable). */
	@Input() displayWith: (option: any) => string = (option) =>
		option === null || option === undefined ? "" : String(option);

	/**
	 * Predicado de filtrado opcional `(opción, términoEnMinúsculas) => boolean`.
	 * Si se omite, se filtra por substring sobre `displayWith` (suficiente para la
	 * mayoría). Útil para búsquedas multi-campo (p.ej. empleado por nombre/email/código).
	 */
	@Input() filterWith?: (option: any, query: string) => boolean;

	/**
	 * Plantilla opcional para renderizar cada opción del desplegable (fila rica).
	 * Recibe la opción como `$implicit`. Si se omite, se muestra `displayWith(opción)`.
	 */
	@Input() optionTpl?: TemplateRef<{ $implicit: any }>;

	/** Prefijo opcional del campo (sustituye al icono de lupa: p.ej. avatar de la selección). */
	@Input() prefixTpl?: TemplateRef<unknown> | null;

	/** Sufijo opcional del campo (tras el botón de limpiar: p.ej. icono info con tooltip). */
	@Input() suffixTpl?: TemplateRef<unknown> | null;

	/** Máximo de opciones renderizadas en el panel (0 = sin límite). Rendimiento en listas grandes. */
	@Input() maxOptions = 0;

	/** Texto del pie cuando hay más resultados de los mostrados: `(mostrados, total) => string`. */
	@Input() moreText?: (shown: number, total: number) => string;

	/** Estado de carga (muestra spinner en el campo y mensaje en el panel). */
	@Input() loading = false;
	@Input() loadingText = "Cargando…";

	/** Opción actualmente seleccionada (objeto), o null. */
	@Input() set value(val: any) {
		this._value = val ?? null;
		this.text = this._value ? this.displayWith(this._value) : "";
	}
	get value(): any {
		return this._value;
	}
	private _value: any = null;

	@Output() valueChange = new EventEmitter<any>();
	@Output() touched = new EventEmitter<void>();
	/** Limpieza EXPLÍCITA (botón X) — distinta de borrar el texto a mano. */
	@Output() cleared = new EventEmitter<void>();

	@ViewChild("input") inputRef!: ElementRef<HTMLInputElement>;

	text = "";
	isOpen = false;
	filtered: any[] = [];
	totalMatches = 0;
	activeIndex = -1;
	panelWidth = 0;

	constructor(
		private readonly cdr: ChangeDetectorRef,
		private readonly host: ElementRef<HTMLElement>,
	) {}

	open(): void {
		if (this._disabled) {
			return;
		}
		const box = this.host.nativeElement.querySelector(".dsac-box");
		this.panelWidth = box ? box.getBoundingClientRect().width : 0;
		this.filter(this.text, true);
		this.isOpen = true;
		// Con una selección vigente, el texto se preselecciona entero: teclear
		// reemplaza el nombre actual y busca de cero (sin tener que borrarlo a mano).
		if (
			this._value !== null &&
			this.text &&
			this.displayWith(this._value) === this.text
		) {
			setTimeout(() => this.inputRef?.nativeElement?.select(), 0);
		}
		this.cdr.markForCheck();
	}

	close(): void {
		this.isOpen = false;
		this.activeIndex = -1;
		this.cdr.markForCheck();
	}

	onInput(event: Event): void {
		this.text = (event.target as HTMLInputElement).value;
		this.filter(this.text, false);
		this.isOpen = true;
		if (!this.text) {
			this.setValue(null);
		}
		this.cdr.markForCheck();
	}

	private filter(query: string, allowAllWhenMatches: boolean): void {
		const term = (query || "").trim().toLowerCase();
		const matchesSelection =
			allowAllWhenMatches &&
			this._value !== null &&
			this.displayWith(this._value) === query;
		if (!term || matchesSelection) {
			this.filtered = [...this.options];
		} else if (this.filterWith) {
			this.filtered = this.options.filter((option) =>
				this.filterWith!(option, term),
			);
		} else {
			this.filtered = this.options.filter((option) =>
				this.displayWith(option).toLowerCase().includes(term),
			);
		}
		this.totalMatches = this.filtered.length;
		if (this.maxOptions > 0 && this.filtered.length > this.maxOptions) {
			this.filtered = this.filtered.slice(0, this.maxOptions);
		}
		this.activeIndex = this.filtered.length ? 0 : -1;
	}

	select(option: any): void {
		this.setValue(option);
		this.text = this.displayWith(option);
		this.close();
	}

	clear(): void {
		this.setValue(null);
		this.text = "";
		this.filter("", false);
		this.cleared.emit();
		this.touched.emit();
		if (this.inputRef?.nativeElement) {
			this.inputRef.nativeElement.focus();
		}
		this.cdr.markForCheck();
	}

	private setValue(option: any): void {
		this._value = option;
		this.valueChange.emit(option);
	}

	onBlur(): void {
		this.touched.emit();
		// Si el texto no corresponde a la selección, se revierte (no se aceptan textos libres).
		setTimeout(() => {
			this.text = this._value ? this.displayWith(this._value) : "";
			this.cdr.markForCheck();
		}, 150);
	}

	onOutsideClick(): void {
		this.close();
	}

	onKeydown(event: KeyboardEvent): void {
		if (this._disabled) {
			return;
		}
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				if (!this.isOpen) {
					this.open();
				} else {
					this.move(1);
				}
				break;
			case "ArrowUp":
				event.preventDefault();
				this.move(-1);
				break;
			case "Enter":
				if (
					this.isOpen &&
					this.activeIndex >= 0 &&
					this.filtered[this.activeIndex]
				) {
					event.preventDefault();
					this.select(this.filtered[this.activeIndex]);
				}
				break;
			case "Escape":
				if (this.isOpen) {
					event.preventDefault();
					this.close();
				}
				break;
			default:
				break;
		}
	}

	private move(delta: number): void {
		if (!this.filtered.length) {
			return;
		}
		this.activeIndex =
			(this.activeIndex + delta + this.filtered.length) %
			this.filtered.length;
		this.cdr.markForCheck();
		setTimeout(() => this.scrollActiveIntoView(), 0);
	}

	private scrollActiveIntoView(): void {
		const panel = document.querySelector(".dsac-panel");
		const option = panel?.querySelectorAll(".dsac-opt")[
			this.activeIndex
		] as HTMLElement | undefined;
		option?.scrollIntoView({ block: "nearest" });
	}
}
